/**
 * The headless driver (ADR 0010): replays a golden log through game-core and
 * compares the final state hash. No terminal, no files, no process exit, so
 * tests, the browser and later the full-loop client can call it directly. The
 * commands apply synchronously; only the hash (WebCrypto) is asynchronous.
 */
import { type ApplyResult, type Command, type Event, type Game, type RunState, InvalidCommand } from '@ab/game-core';
import { type GoldenLog, stateHash } from './golden.ts';

export type ReplayStep = {
  readonly command: Command;
  readonly before: RunState;
  readonly after: RunState;
  readonly events: readonly Event[];
};

export type ReplayResult = {
  /** True when every command applied and the final hash equals the recorded one. */
  readonly match: boolean;
  readonly expected: string;
  /** The hash of the state the replay reached (the last valid state if a command failed). */
  readonly actual: string;
  readonly steps: readonly ReplayStep[];
  readonly state: RunState;
  /** Set when a command was rejected: its index and the `InvalidCommand` message. */
  readonly error: { readonly index: number; readonly message: string } | null;
};

export const replayLog = async (game: Game, log: GoldenLog): Promise<ReplayResult> => {
  let state = game.createRun(log.dungeon, log.party, log.bag ?? []);
  const steps: ReplayStep[] = [];
  let error: ReplayResult['error'] = null;
  for (const [index, command] of log.commands.entries()) {
    let r: ApplyResult;
    try {
      r = game.apply(state, command);
    } catch (e) {
      if (!(e instanceof InvalidCommand)) throw e;
      error = { index, message: e.message };
      break;
    }
    steps.push({ command, before: state, after: r.state, events: r.events });
    state = r.state;
  }
  const actual = await stateHash(state);
  return { match: error === null && actual === log.hash, expected: log.hash, actual, steps, state, error };
};
